import bindAllOfThis from 'src/utils/BindThisHelper';
import ImageApi from 'src/utils/ImageHelper';
import { PhaserColorWheel } from '../../shared/ColorWheel';
import { TiledGameObject } from 'src/games/shared/phaserHelpers/HandyTypes';
import TranslateApi from 'src/utils/TranslateHelper';
import { EndGameScene } from './EndGameScene';

// Paintbrush helper class to hold drawing attributes
class PaintBrush {
    public thickness: number;
    public color: number;
    public alpha: number;

    // default color yellow, thickness 5
    constructor() {
        this.thickness = 10;
        this.color = 0x223e93;
        this.alpha = 1;
    }
}

export class MainScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    map: Phaser.Tilemaps.Tilemap;

    private easelControls: Phaser.Geom.Rectangle;
    private paintBrush: PaintBrush = new PaintBrush();
    private lineHistory: Phaser.Geom.Point[];
    private drawing: boolean;
    private finishButton: Phaser.GameObjects.Sprite;
    private cake: Phaser.GameObjects.Image;
    private frostingOverlay: Phaser.GameObjects.Image;
    private cakeTexture: Phaser.GameObjects.RenderTexture;
    private cakeGraphic: Phaser.GameObjects.Graphics;
    private cakePolygon: Phaser.Geom.Polygon;
    private div: HTMLDivElement = document.createElement('div');
    private currentSticker: Phaser.GameObjects.Image;
    private paste: Phaser.GameObjects.Sprite;
    private pastedImages: Phaser.GameObjects.Image[];
    private frostingGunCursor: string = 'url(' + require("/src/assets/qci/sprites/cursors/frosting_gun.cur") + '), pointer';
    private drawingMode: boolean = false;

    public static CROPPED_IMAGE: string = null;

    constructor() {
        super({
            key: "MainScene"
        });
    }

    preload(): void {
        this.load.image("background", require("src/assets/qci/backgrounds/ka_background.png"));
        this.load.tilemapTiledJSON("map", require('src/assets/qci/maps/cake_decorating.json'));
        this.load.atlas("assets", require("src/assets/qci/atlas/gameCakeDecorating.png"), require("src/assets/qci/atlas/gameCakeDecorating.json"));
        PhaserColorWheel.preload(this);
    }

    create(): void {
        bindAllOfThis(this, MainScene.prototype);
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        this.input.setDefaultCursor(this.frostingGunCursor);
        this.map = this.make.tilemap({ key: "map" });

        this.buildBackgroundContent();

        this.lineHistory = [];
        this.pastedImages = [];
        this.drawing = false;
        this.drawingMode = true;
        document.body.appendChild(this.div);

        // Register the click events
        this.registerEvents();
    }

    buildBackgroundContent() {
        let background = this.add.image(0, 0, "background").setOrigin(0);
        this.easelControls = new Phaser.Geom.Rectangle(815, 0, this.gameWidth - 815, 768);

        var ob = this.map.getObjectLayer("Scene").objects.find((ob: TiledGameObject) => ob.name === "cake") as TiledGameObject;
        this.cake = this.add.image(ob.x + ob.width / 2, ob.y + ob.height / 2, "assets", "cake");

        this.frostingOverlay = this.add.image(ob.x + ob.width / 2, ob.y + ob.height / 2, "assets", "frosting2");
        this.frostingOverlay.setInteractive();

        this.currentSticker = this.add.image(0, 0, "assets", "sprinkles1");
        this.currentSticker.setDepth(2);
        this.currentSticker.setVisible(false);

        this.buildEasel();
        this.buildButton();
        this.buildColorWheel();
        this.buildSprinkles();
        this.buildFrostings();
        this.buildCandles();
        this.buildText();
    }

    buildEasel(): void {
        var ob = this.map.getObjectLayer("Scene").objects.find((ob: TiledGameObject) => ob.name === "cakePoly") as TiledGameObject;
        var points = ob["polygon"];
        var topLeft = this.cake.getTopLeft();
        points.forEach((p) => {
            p.x += ob.x;
            p.y += ob.y;
        });

        this.cakePolygon = new Phaser.Geom.Polygon(points);

        this.frostingOverlay.on('pointerdown', (pointer) => {
            this.pasteImage(pointer);
        });

        this.cakeTexture = this.add.renderTexture(0, 0, this.gameWidth, this.gameHeight);
        this.cakeTexture.setOrigin(0);
        this.cakeTexture.setDepth(2);

        this.cakeGraphic = this.add.graphics();
        this.cakeGraphic.setDepth(2);
    }

    buildText() {
        var style = { fontFamily: "Russo One, Arial", fontSize: 22, align: "center" };
        this.map.getObjectLayer("Text").objects.forEach((ob: TiledGameObject) => {
            var key = "CakeDecorating.Phaser." + ob.name;
            var text = this.add.text(ob.x, ob.y, TranslateApi.translate(key), style);
            text.setWordWrapWidth(200, true);
        });
    }

    buildFrostings() {
        this.map.getObjectLayer("Frostings").objects.forEach((ob: TiledGameObject) => {
            var id = ob.properties["id"];
            var frosting = this.add.image(ob.x + ob.width / 2, ob.y + ob.height / 2, "assets", "frostingPicker" + id);
            frosting.setInteractive({ useHandCursor: true });
            frosting.on("pointerdown", () => {
                this.frostingOverlay.setFrame("frosting" + id);
            });
        });
    }

    buildCandles() {
        var id = 1;
        this.map.getObjectLayer("Candles").objects.forEach((ob: TiledGameObject) => {
            var candle = this.add.sprite(ob.x + ob.width / 2, ob.y + ob.height / 2, "assets", "candle"+ id + "1");
            candle.setInteractive({ useHandCursor: true });
            candle.on("pointerdown", () => {
                this.drawingMode = false;
                this.drawing = false;
                this.currentSticker.setFrame(candle.frame.name);
                this.currentSticker.setVisible(true);
                this.input.setDefaultCursor('none');
                this.children.bringToTop(this.currentSticker);
            });
            candle.setDepth(2);
            this.anims.create({
                key: 'candle' + id,
                frames: this.anims.generateFrameNames('assets', { prefix: 'candle' + id, start: 1, end: 2 }),
                frameRate: 3,
                repeat: -1
            });
            candle.anims.play('candle' + id, true, Phaser.Math.Between(1, 2));
            id++;
        });
    }

    buildSprinkles() {
        var id = 1;
        this.map.getObjectLayer("Sprinkles").objects.forEach((ob: TiledGameObject) => {
            var sprinkles = this.add.image(ob.x + ob.width / 2, ob.y + ob.height / 2, "assets", "sprinkles"+id++);
            sprinkles.setInteractive({ useHandCursor: true });
            sprinkles.on("pointerdown", () => {
                this.drawingMode = false;
                this.drawing = false;
                this.currentSticker.setFrame(sprinkles.frame.name);
                this.currentSticker.setVisible(true);
                this.input.setDefaultCursor('none');
                this.children.bringToTop(this.currentSticker);
            });
            sprinkles.setDepth(2);
        });
    }

    private canPaste(): boolean {
        // Don't even have a sticker right now
        if (!this.currentSticker.visible) {
            return false;
        }

        return this.inCakeArea();
    }

    inCakeArea(): boolean {
        var bounds = this.currentSticker.getBounds();
        var center = this.currentSticker.getCenter();
        bounds.width *= 0.1;
        bounds.height *= 0.1;
        bounds.centerX = center.x;
        bounds.centerY = center.y;

        if (this.cakePolygon.contains(center.x, center.y)) {
            return true;
        }

        // Check if it contains any of the 4 corners (polygons don't have an intersects method)
        if (this.cakePolygon.contains(bounds.left, bounds.bottom) ||
            this.cakePolygon.contains(bounds.right, bounds.bottom) ||
            this.cakePolygon.contains(bounds.left, bounds.top) ||
            this.cakePolygon.contains(bounds.right, bounds.top)) {
            return true;
        }

        return false;
    }

    pasteImage(pointer) {
        if (this.canPaste()) {
            this.paste = this.add.sprite(pointer.x, pointer.y, "assets", this.currentSticker.frame.name);
            this.paste.setInteractive();
            this.input.setDraggable(this.paste);
            this.pastedImages.push(this.paste);
            this.paste.setDepth(this.cakeTexture.depth + 0.1);
            if(this.currentSticker.frame.name.startsWith("candle")) {
                var anim = this.currentSticker.frame.name.slice(0, this.currentSticker.frame.name.length - 1);
                this.paste.anims.play(anim);
            }
        }
    }

    buildButton(): void {
        var ob = this.map.getObjectLayer("Scene").objects.find((ob: TiledGameObject) => ob.name === "button") as TiledGameObject;
        this.finishButton = this.add.sprite(ob.x + ob.width / 2, ob.y + ob.height / 2, "assets", "button");
        this.finishButton.setInteractive({ useHandCursor: true });
        this.finishButton.on("pointerdown", () => {
            this.drawingMode = false;
            this.currentSticker.setVisible(false);
            // Screenshot for later
            this.registry.parent.renderer.snapshot((image: HTMLImageElement) => {
                image.onload = () => {
                    var croppedImage = ImageApi.CropImageToDataURI(image, 0, 0, this.sys.canvas.width - 300, this.sys.canvas.height);
                    console.warn("Screenshot!", croppedImage); // JB - Remove this console.warn
                    this.endGame(croppedImage);
                };
            });
        });

        var style = { fontSize: 18, fontFamily: "Russo One, Arial", color: "#5a108a" };
        var text = this.add.text(0, 0, TranslateApi.translate("common.done"), style);
        Phaser.Display.Align.In.Center(text, this.finishButton);
    }

    buildColorWheel(): void {
        var ob = this.map.getObjectLayer("Scene").objects.find((ob: TiledGameObject) => ob.name === "colorwheel") as TiledGameObject;
        var colorWheel = new PhaserColorWheel(this, ob.x, ob.y, ob.width, ob.height);
        colorWheel.on("colorChange", (color: Phaser.Display.Color) => {
            this.drawingMode = true;
            this.paintBrush.color = color.color;
            this.refreshPaintBrush();
            this.input.setDefaultCursor(this.frostingGunCursor);
            this.currentSticker.setVisible(false);
        });
        colorWheel.setDepth(2);
    }

    createSticker(x, y, id) {
        var sticker = this.add.image(x, y, "assets", "sticker" + id);
        sticker.setDepth(2);
        sticker.setInteractive();
        sticker.on("pointerdown", () => {
            this.drawingMode = false;
            this.drawing = false;
            this.currentSticker.setFrame(sticker.frame.name);
            this.currentSticker.setVisible(true);
            this.input.setDefaultCursor('none');
            this.children.bringToTop(this.currentSticker);
        });
    }

    refreshPaintBrush() {
        this.cakeGraphic.lineStyle(this.paintBrush.thickness, this.paintBrush.color, this.paintBrush.alpha);
    }

    registerEvents(): void {
        this.input.on('pointerdown', this.pointerDown);
        this.input.on('pointerup', this.pointerUp);
        this.input.on('pointermove', this.pointerMove);
        this.input.on('dragstart', (pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.Image) => {
            if (!this.drawingMode) {
                this.children.bringToTop(gameObject);
                this.drawingMode = false;
                gameObject.x = pointer.downX;
                gameObject.y = pointer.downY;
            }
        });

        this.input.on('drag', (pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.Image, dragX: number, dragY: number) => {
            if (this.drawingMode) {
                return;
            }
            gameObject.x = pointer.x;
            gameObject.y = pointer.y;
        });

        this.input.on('dragend', (pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.Image) => {
            // If we're drawing, no need to do anything
            if (this.drawing || this.drawingMode) {
                return;
            }

            if (!this.inCakeArea()) {
                gameObject.destroy();
            }
            else {
                this.children.moveDown(gameObject);
            }
        });
    }

    canDraw(pointer: Phaser.Input.Pointer) {
        if (this.cakePolygon.contains(pointer.x, pointer.y)) {
            return true;
        }

        return false;
    }

    pointerDown(pointer: Phaser.Input.Pointer): void {
        if (this.drawingMode && this.canDraw(pointer)) {
            this.drawing = true;
        }

        this.lineHistory = [new Phaser.Geom.Point(pointer.x, pointer.y)];
    }

    pointerUp(pointer: Phaser.Input.Pointer): void {
        this.drawing = false;
        // Capture the graphics as a texture
        this.cakeGraphic.generateTexture("background-drawn", this.sys.canvas.width, this.sys.canvas.height);
        this.cakeGraphic.clear();

        let tempImage: Phaser.GameObjects.Image = this.add.image(0, 0, "background-drawn");
        this.cakeTexture.save();
        this.cakeTexture.draw(tempImage.texture, tempImage.frame, 0, 0);
        this.cakeTexture.restore();

        tempImage.destroy();
    }

    pointerMove(pointer: Phaser.Input.Pointer): void {
        if (this.drawing && this.canDraw(pointer)) {
            this.cakeGraphic.clear();
            this.refreshPaintBrush();
            this.lineHistory.push(new Phaser.Geom.Point(pointer.x, pointer.y));
            // Do the drawing
            this.cakeGraphic.strokePoints(this.lineHistory);
        }

        this.currentSticker.x = pointer.x;
        this.currentSticker.y = pointer.y;

        if (this.drawingMode) {
            this.currentSticker.setVisible(false);
            this.input.setDefaultCursor(this.frostingGunCursor);
        }
        else {
            this.currentSticker.setVisible(true);
            this.input.setDefaultCursor("none");
            if (this.isMovingExistingImage(pointer)) {
                this.currentSticker.setVisible(false);
                this.input.setDefaultCursor('-webkit-grabbing');
            }
        }

        if (this.easelControls.contains(this.input.activePointer.x, this.input.activePointer.y)) {
            this.currentSticker.setVisible(false);
            this.input.setDefaultCursor('pointer');
        }
    }

    isMovingExistingImage(pointer: Phaser.Input.Pointer): boolean {
        for (var i of this.pastedImages) {
            if (i.getBounds().contains(pointer.x, pointer.y)) {
                return true;
            }
        }
        return false;
    }

    endGame(image: string): void {
        MainScene.CROPPED_IMAGE = image;

        this.input.setDefaultCursor("default");
        this.input.removeAllListeners();
        this.scene.add("EndGameScene", EndGameScene, true);
        this.scene.remove(this);
    }

    drawPolygon(polygon) {
        // Draw polygon and set it up
        var graphics = this.add.graphics();
        graphics.lineStyle(2, 0x000000);
        graphics.beginPath();
        graphics.moveTo(polygon.points[0].x, polygon.points[0].y);
        for (var i = 1; i < polygon.points.length; i++) {
            graphics.lineTo(polygon.points[i].x, polygon.points[i].y);
        }
        graphics.closePath();
        graphics.strokePath();
    }
}



// WEBPACK FOOTER //
// ./src/games/CakeDecorating/scenes/MainScene.ts